import { Form } from "@ui/components/Form";
import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import classNames from "classnames";

import type { ButtonSize } from "@jti/core-components/components/Button";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import type { ReferralCodeSubmissionSource } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { useLoyaltyReferralCode } from "../../hooks/useLoyaltyReferralCode";
import { LoyaltyReferralCodeForm } from "../LoyaltyReferralCodeForm/LoyaltyReferralCodeForm";

import styles from "./LoyaltyReferralCodeExtended.module.scss";

type LoyaltyReferralCodeExtendedType = "default" | "profileContainer";

export type LoyaltyReferralCodeExtendedProps = {
  discountImageDesktop: string;
  discountImageTablet: string;
  discountImageMobile: string;
  successImageDesktop: string;
  successImageMobile: string;
  successHeading: string;
  successLabel: string;
  successBtnLabel: string;
  successLink: string;
  heading: string;
  label: string;
  list: Array<{ value: string }> | string[];
  legalText: string;
  discountHeading: string;
  discountLabel: string;
  discountSubText: string;
  validationRequiredMessage: string;
  verifyErrorMessage: string;
  inputLabel: string;
  inputPlaceholder: string;
  inputHelperText: string;
  btnText: string;
  isInEditor: boolean;
  isHidden: boolean;
  type?: LoyaltyReferralCodeExtendedType;
  source?: ReferralCodeSubmissionSource;
};

export const LoyaltyReferralCodeExtended = ({
  discountImageDesktop = "/content/dam/ploom/automation/loyalty/mgm-gradient-referral-desktop.png",
  discountImageTablet = "",
  discountImageMobile = "",
  successImageDesktop = "",
  successImageMobile = "",
  successHeading = "Referral Code Applied!",
  successLabel = "You're all set to receive your reward. Make your first purchase to enjoy [REWARD DESCRIPTION].",
  successBtnLabel = "Shop now",
  successLink = "/en/shop",
  heading = "Have a referral code?",
  label = "Get [Reward] when making your first online purchase.",
  list = [{ value: "Enter the referral code." }, { value: "Receive [Reward]." }],
  legalText = "*Purchase must include a Ploom device",
  discountHeading = "20%",
  discountLabel = "OFF First order",
  discountSubText = "Apply Referral Code Now!",
  validationRequiredMessage = "This field is required",
  verifyErrorMessage = "Invalid code. Please try again.",
  inputLabel = "Referral code",
  inputPlaceholder = "Enter code",
  inputHelperText = "e.g ABCD123",
  btnText = "Apply",
  isInEditor,
  isHidden = false,
  type = "default",
  source = "account_dashboard",
}: LoyaltyReferralCodeExtendedProps) => {
  const { verifyCode, updateReferralCode, onSubmit, codeCustomErrorMessage, form, isSuccess, hideCard } =
    useLoyaltyReferralCode({
      onCodeApplied: () => {},
      verifyErrorMessage,
      whenHasParentCode: () => {},
      source,
    });

  const normalizeArray = (input: LoyaltyReferralCodeExtendedProps["list"]) => {
    return input?.map((item) =>
      typeof item === "object" && item !== null && "value" in item ? item.value : String(item)
    );
  };

  const { mainBreakpoint, specificBreakpoint } = useCurrentBreakpoint();

  const isMobileOrTablet = ["xs", "sm"].includes(mainBreakpoint) || specificBreakpoint === "medium";

  const disabled = !form.formState.isValid || updateReferralCode.isLoading || verifyCode.isLoading || isSuccess;

  const discountImagesByBreakpoint: Record<string, string> = {
    xs: discountImageMobile,
    sm: discountImageTablet,
    md: discountImageDesktop,
  };
  const discountImage = discountImagesByBreakpoint[mainBreakpoint] || discountImageDesktop;

  const successImagesByBreakpoint: Record<string, string> = {
    xs: successImageMobile || successImageDesktop,
    sm: successImageMobile || successImageDesktop,
    md: successImageDesktop,
  };
  const successImage = successImagesByBreakpoint[mainBreakpoint] || successImageDesktop;
  const buttonSizeByBreakpoint: Record<string, ButtonSize> = { xs: "sm", sm: "md" };
  const buttonSize: ButtonSize = buttonSizeByBreakpoint[mainBreakpoint] ?? "md";

  if (isHidden || (hideCard && !isInEditor)) {
    return null;
  }

  const discountBlock = (
    <div className={styles.discountImage} style={{ backgroundImage: `url(${discountImage})` }}>
      <div className={styles.discountSpacing}></div>
      <div className={styles.heading}>
        <span className={styles.discountHeading}>{discountHeading}</span>
        <span className={styles.discountSubHeading}>{discountLabel}</span>
      </div>
      <span className={styles.discountSubText}>{discountSubText}</span>
    </div>
  );

  const handleSuccessCtaClick = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: successBtnLabel,
      cta_location: "loyalty-referral-code-extended",
    });
  };

  const successBlock = (
    // TODO: DESP-92637 Change this implementation once proper PushBlock variant is available
    <div
      className={classNames(styles.successImage, {
        [styles.clubContainer]: type === "default",
      })}
      style={{ "--success-image": `url(${successImage})` } as React.CSSProperties}
    >
      <div className={classNames(styles.successCard, "dark-theme")}>
        <h4 className={styles.successHeading}>{successHeading}</h4>
        <p className={styles.successDescription}>{successLabel}</p>
        <Button
          href={successLink}
          nativeBehaviour
          size={buttonSize}
          variant="primary"
          data-testid="successCta"
          onClick={handleSuccessCtaClick}
        >
          {successBtnLabel}
        </Button>
      </div>
    </div>
  );

  const cardHeading = isSuccess ? successHeading : heading;
  if (isSuccess && successImageDesktop) {
    return successBlock;
  }

  return (
    <div className={styles.card}>
      <Card variant="level-1-club" data-testid="loyaltyReferralCode">
        {isSuccess && <h4 className={styles.headingText}>{cardHeading}</h4>}
        <div className={styles.innerContainer}>
          {isSuccess ? (
            <div className={styles.success}>
              <p className={styles.successLabel}>{successLabel}</p>
              <div className={styles.submitContainer}>
                <Button
                  href={successLink}
                  variant="primary"
                  data-testid="successCta"
                  onClick={handleSuccessCtaClick}
                  nativeBehaviour
                  size={buttonSize}
                >
                  {successBtnLabel}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className={classNames(styles.block, styles.referralInfo)}>
                <h4 className={styles.headingText}>{cardHeading}</h4>
                {isMobileOrTablet && discountBlock}
                <div className={styles.textContainer}>
                  <span>{label}</span>
                  <ol className={styles.rewardFlowList}>
                    {normalizeArray(list)?.map((item: string) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ol>
                  <p className={styles.legalText}>{legalText}</p>
                </div>
                <Form form={form} onSubmit={onSubmit}>
                  <LoyaltyReferralCodeForm
                    isSuccess={isSuccess}
                    disabled={disabled}
                    error={codeCustomErrorMessage ?? null}
                    requiredText={validationRequiredMessage}
                    label={inputLabel}
                    placeholder={inputPlaceholder}
                    helperText={inputHelperText}
                    successText=""
                    btnLabel={btnText}
                    btnVariant="primary"
                    inputContainerClassname={styles.inputContainer}
                    contentWrapperClassname={styles.contentWrapper}
                    buttonSize={buttonSize}
                  />
                </Form>
              </div>
              {!isMobileOrTablet && (
                <div className={classNames(styles.block, styles.referralDiscount)}>{discountBlock}</div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
